// src/hooks/useFailures.ts
//
// React data hook for the Settings → Failures view (Story 4.8). Plain
// useState/useCallback/useEffect per the repo's hook style (useTickets/useRegions)
// — no React Query. All data access goes through failuresService; this view has NO
// mutations at all (reads only — Decision 9).
//
// The four feeds are fetched in parallel and merged by the pure mergeFailureRows
// helper. Live updates: postgres_changes subscriptions on yak_dead_letters,
// yak_notifications, yak_calls and yak_call_verifications each drive a SILENT
// refetch (no loading flash, no focus stealing).
//
// Silent-refresh non-contradiction (copied from useTickets/useRegions): only the
// initial/explicit load owns the error banner. A failed background refetch leaves
// the last-known rows in place and only logs — surfacing it would contradict a
// view that is already showing valid data.

import { useState, useEffect, useCallback } from 'react'
import { failuresService } from '../services/failuresService'
import { useRealtimeRefetch } from './useRealtimeRefetch'
import { type FailureRow, mergeFailureRows } from '../utils/failureDisplay'

interface UseFailuresReturn {
  rows: FailureRow[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useFailures(): UseFailuresReturn {
  const [rows, setRows] = useState<FailureRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAll = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      setError(null)
      // allSettled, not all (Story 6.2 review): 6.2 added a fourth feed against
      // yak_call_verifications, a table that does not exist in the live database
      // until migration 044 is applied. Under Promise.all one rejecting feed
      // discarded the other three, so a console deployed ahead of 044 showed an
      // error banner instead of the dead letters, outbox failures and
      // unrecognized-caller signals that were all fetching fine. A feed that
      // fails now degrades to empty and says so, rather than blanking the page.
      const settled = await Promise.allSettled([
        failuresService.listDeadLetters(),
        failuresService.listFailedNotifications(),
        failuresService.listUnrecognizedCalls(),
        failuresService.listVerificationLockouts(),
      ])
      const FEED_NAMES = [
        'dead letters',
        'delivery failures',
        'unrecognized callers',
        'verification lockouts',
      ]
      const failed: string[] = []
      settled.forEach((r, i) => {
        if (r.status === 'rejected') {
          failed.push(FEED_NAMES[i])
          console.error(`Failure feed "${FEED_NAMES[i]}" failed`, r.reason)
        }
      })
      const [deadLetters, failedNotifications, calls, verifications] =
        settled.map(r => (r.status === 'fulfilled' ? r.value : [])) as [
          Awaited<ReturnType<typeof failuresService.listDeadLetters>>,
          Awaited<ReturnType<typeof failuresService.listFailedNotifications>>,
          Awaited<ReturnType<typeof failuresService.listUnrecognizedCalls>>,
          Awaited<ReturnType<typeof failuresService.listVerificationLockouts>>,
        ]
      setRows(
        mergeFailureRows(deadLetters, failedNotifications, calls, verifications)
      )
      // Partial failure is still worth naming — silently showing three of four
      // feeds would read as "nothing else is wrong".
      if (failed.length > 0 && !silent) {
        setError(`Could not load: ${failed.join(', ')}`)
      }
    } catch (err) {
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Failed to load failures')
      } else {
        console.error('Failure list refresh failed', err)
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const silentRefetch = useCallback(() => {
    fetchAll(true)
  }, [fetchAll])

  // Four channels, each cleaned up on unmount (useRealtimeRefetch tears them down).
  useRealtimeRefetch('yak_dead_letters', silentRefetch)
  useRealtimeRefetch('yak_notifications', silentRefetch)
  useRealtimeRefetch('yak_calls', silentRefetch)
  // Story 6.2. This view is the ONLY subscriber to yak_call_verifications, so
  // 4.9's channelKey disambiguation isn't load-bearing here — but it costs
  // nothing and it keeps the channel name unambiguous if a second consumer ever
  // appears. Realtime events still respect RLS, so a console user only ever sees
  // the locked-out rows migration 044's policy exposes.
  useRealtimeRefetch('yak_call_verifications', silentRefetch, 'failures')

  return { rows, loading, error, refetch: () => fetchAll() }
}
